import React, { useCallback, useEffect, useRef, useState } from "react";
import { ScanLine, Search, Loader2, CarFront, IdCard, LogIn, IndianRupee, Users, ShieldAlert, Clock, Plus } from "lucide-react";
import { toast } from "sonner";
import { Spinner } from "../../components/common";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { api, errMsg, fmtINR, fmtDay } from "../../lib/api";
import { pokeLive } from "../../lib/useLive";
import { AvailabilityBars, PStatus, VehicleTypeBadge, VT_META, VT_KEYS, fmtMinutes, occupancyTone } from "../../components/parking/common";
import { ReceiptDialog } from "../../components/parking/Ticket";
import { PostingStrip } from "../../components/layouts/MarshalLayout";
import { MarshalPayModes, QrScanDialog, notifyDone, useMarshal, useMarshalDashboard } from "./marshalShared";
import { BookingSheet } from "./BookingSheet";

/* Phase 202 — Gate screen: scan / type a code → booking or pass actions; on-spot booking; live occupancy + today's
   numbers for this marshal. */

export default function MarshalGate() {
  const { code, me } = useMarshal();
  const { dash, refresh } = useMarshalDashboard();
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);     // lookup response
  const [sheet, setSheet] = useState(null);       // { booking, warning }
  const [scan, setScan] = useState(false);
  const [onSpot, setOnSpot] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const inputRef = useRef(null);

  const lookup = useCallback(async (value) => {
    const v = (value ?? q).trim();
    if (v.length < 4) { toast.error("Enter at least 4 characters (QR, booking no, pass no or vehicle no)"); return; }
    setBusy(true); setResult(null);
    try {
      const r = await api.post(`/t/${code}/parking/marshal/lookup`, { code: v });
      setResult(r.data);
      if (r.data.booking && !r.data.pass) setSheet({ booking: r.data.booking, warning: r.data.warning });
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  }, [code, q]);

  const onScanned = useCallback((v) => { setScan(false); setQ(v); lookup(v); }, [lookup]);

  const passEntry = async (p) => {
    setBusy(true);
    try {
      const r = await api.post(`/t/${code}/parking/marshal/pass-entry`, { pass_id: p.id });
      toast.success(`${r.data.booking.vehicle_number} entered on pass ${p.pass_no}`);
      pokeLive(code, "marshal"); setResult(null); setQ(""); refresh();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const afterAction = () => { setSheet(null); setResult(null); setQ(""); refresh(); };

  if (!me) return <Spinner label="Loading your posting…" />;
  const occ = dash?.occupancy;
  const st = dash?.stats;
  return (
    <div className="space-y-4" data-testid="marshal-gate">
      <PostingStrip location={dash?.location || me.location} shift={dash?.shift_now ?? me.shift_now} occupancy={occ} />

      <Card className="shadow-sm border-primary/30">
        <CardContent className="p-4 space-y-3">
          <Label htmlFor="gate-code" className="text-xs">QR token · booking no · pass no · vehicle no</Label>
          <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); lookup(); }}>
            <Input id="gate-code" ref={inputRef} value={q} onChange={(e) => setQ(e.target.value.toUpperCase())} placeholder="e.g. CG07AB1234 or PK-2026…" className="h-12 font-mono text-base" autoComplete="off" data-testid="gate-code-input" />
            <Button type="submit" className="h-12 px-4" disabled={busy} data-testid="gate-lookup-button">{busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Search className="h-5 w-5" />}</Button>
          </form>
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" className="h-12" onClick={() => setScan(true)} data-testid="gate-scan-button"><ScanLine className="h-5 w-5 mr-2" /> Scan QR</Button>
            <Button className="h-12" onClick={() => setOnSpot(true)} data-testid="gate-on-spot-button"><CarFront className="h-5 w-5 mr-2" /> On-spot entry</Button>
          </div>
        </CardContent>
      </Card>

      {result?.pass && (
        <Card className="shadow-sm" data-testid="gate-pass-result">
          <CardContent className="p-4 space-y-2">
            <div className="flex items-center justify-between gap-2"><p className="font-semibold font-heading flex items-center gap-1.5"><IdCard className="h-4 w-4 text-primary" /> Pass {result.pass.pass_no}</p><PStatus status={result.pass.status} /></div>
            <p className="text-sm flex items-center gap-2"><VehicleTypeBadge type={result.pass.vehicle_type} short /><span className="font-mono font-medium">{result.pass.vehicle_number}</span><span className="text-xs text-muted-foreground capitalize ml-auto">{result.pass.pass_type} · {result.pass.location_name || "All locations"}</span></p>
            <p className="text-xs text-muted-foreground">Valid {fmtDay(result.pass.valid_from)} – {fmtDay(result.pass.valid_to)} · {result.pass.citizen_name || ""}</p>
            {result.pass.valid_here_today ? (
              <Button className="w-full h-12" onClick={() => passEntry(result.pass)} disabled={busy} data-testid="gate-pass-entry"><LogIn className="h-4 w-4 mr-2" /> Record pass entry</Button>
            ) : <p className="text-sm text-destructive font-medium" data-testid="gate-pass-invalid">This pass is not valid here today.</p>}
            {result.booking && <Button variant="outline" className="w-full h-11" onClick={() => setSheet({ booking: result.booking, warning: result.warning })} data-testid="gate-open-booking">Open booking {result.booking.booking_no} ({result.booking.status})</Button>}
          </CardContent>
        </Card>
      )}

      {dash ? (
        <>
          <Card className="shadow-sm">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between"><p className="text-sm font-semibold font-heading">Live occupancy</p><span className={`text-sm font-semibold tabular-nums ${occupancyTone(occ?.total?.occupancy_pct || 0)}`} data-testid="gate-occupancy-pct">{occ?.total?.occupancy_pct ?? 0}% full</span></div>
              <AvailabilityBars occupancy={occ} testId="gate-avail" />
              <div className="grid grid-cols-3 gap-2 text-center">
                <Stat label="Inside" value={dash.active.length} testId="gate-stat-inside" />
                <Stat label="Upcoming" value={dash.upcoming.length} testId="gate-stat-upcoming" />
                <Stat label="Overstay" value={dash.overstays.length} tone={dash.overstays.length ? "text-destructive" : ""} testId="gate-stat-overstays" />
              </div>
            </CardContent>
          </Card>
          {dash.overstays.length > 0 && (
            <Card className="shadow-sm border-destructive/30" data-testid="gate-overstays">
              <CardContent className="p-4 space-y-2">
                <p className="text-sm font-semibold font-heading flex items-center gap-1.5 text-destructive"><Clock className="h-4 w-4" /> Overstaying now</p>
                {dash.overstays.slice(0, 5).map((b) => (
                  <button key={b.id} type="button" onClick={() => setSheet({ booking: b })} data-testid={`gate-overstay-${b.booking_no}`} className="w-full flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-sm text-left hover:bg-accent/50">
                    <span className="flex items-center gap-2"><VehicleTypeBadge type={b.vehicle_type} short /><span className="font-mono font-medium">{b.vehicle_number}</span></span>
                    <span className="text-xs text-destructive font-semibold">+{fmtMinutes(b.overstay_minutes)}</span>
                  </button>
                ))}
              </CardContent>
            </Card>
          )}
          <Card className="shadow-sm">
            <CardContent className="p-4">
              <p className="text-sm font-semibold font-heading mb-2">Today · my shift</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <Stat label="Entries / exits" value={`${st.entries} / ${st.exits}`} icon={LogIn} testId="gate-stat-entries" />
                <Stat label="My collection" value={fmtINR(st.my_collection.collected)} sub={`${st.my_collection.receipts} receipt(s)`} icon={IndianRupee} testId="gate-stat-collection" />
                <Stat label="Location revenue" value={fmtINR(st.location_revenue)} icon={Users} testId="gate-stat-revenue" />
                <Stat label="Violations" value={st.violations} icon={ShieldAlert} testId="gate-stat-violations" />
              </div>
              {Object.keys(st.my_collection.by_mode || {}).length > 0 && <p className="text-[11px] text-muted-foreground mt-2">{Object.entries(st.my_collection.by_mode).map(([m, v]) => `${m.toUpperCase()} ${fmtINR(v)}`).join(" · ")}</p>}
            </CardContent>
          </Card>
        </>
      ) : <Spinner />}

      <QrScanDialog open={scan} onClose={() => setScan(false)} onResult={onScanned} />
      {sheet && <BookingSheet booking={sheet.booking} warning={sheet.warning} onClose={() => setSheet(null)} onDone={afterAction} onViewReceipt={setReceipt} />}
      {onSpot && <OnSpotDialog occupancy={occ} onClose={() => setOnSpot(false)} onDone={() => { setOnSpot(false); refresh(); }} onViewReceipt={setReceipt} />}
      <ReceiptDialog receipt={receipt} onClose={() => setReceipt(null)} />
    </div>
  );
}

const Stat = ({ label, value, sub, icon: Icon, tone = "", testId }) => (
  <div className="rounded-lg bg-accent/40 px-3 py-2 text-left" data-testid={testId}>
    <p className="text-[11px] text-muted-foreground flex items-center gap-1">{Icon && <Icon className="h-3 w-3" />}{label}</p>
    <p className={`text-lg font-semibold tabular-nums font-heading leading-tight ${tone}`}>{value}</p>
    {sub && <p className="text-[11px] text-muted-foreground">{sub}</p>}
  </div>
);

export function OnSpotDialog({ occupancy, onClose, onDone, onViewReceipt }) {
  const { code, me } = useMarshal();
  const [vt, setVt] = useState("four_wheeler");
  const [vn, setVn] = useState("");
  const [duration, setDuration] = useState(60);
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [mode, setMode] = useState((me?.payment_modes || ["pos"])[0]);
  const [busy, setBusy] = useState(false);
  const tariff = me?.tariffs?.[vt] || {};
  const fare = Math.round((Number(tariff.first_hour || 0) + Math.max(Math.ceil(duration / 60) - 1, 0) * Number(tariff.subsequent_hour || 0)) * 100) / 100;
  const minM = Number(me?.settings?.min_duration_minutes || 30), maxM = Number(me?.settings?.max_duration_hours || 12) * 60;
  const go = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/t/${code}/parking/marshal/on-spot`, { vehicle_type: vt, vehicle_number: vn, duration_minutes: duration, citizen_name: name || null, citizen_mobile: mobile || null, payment_mode: mode === "none" ? null : mode });
      notifyDone(`${r.data.booking.vehicle_number} entered — ${r.data.booking.booking_no}${r.data.receipt ? ` · ${fmtINR(r.data.receipt.amount)} collected` : " · pay at exit"}`, r.data.receipt, onViewReceipt);
      pokeLive(code, "marshal");
      onDone(r.data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="sm:max-w-md max-h-[92vh] overflow-y-auto" data-testid="on-spot-dialog">
        <DialogHeader><DialogTitle className="font-heading">On-spot entry</DialogTitle><DialogDescription>Walk-in vehicle without an app booking. Collect now or at exit.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-2" data-testid="on-spot-vt">
            {VT_KEYS.map((k) => { const m = VT_META[k]; const a = occupancy?.available?.[k] ?? 0; return (
              <button key={k} type="button" onClick={() => setVt(k)} data-testid={`on-spot-vt-${k}`} className={`rounded-lg border p-2.5 text-left min-h-[56px] ${vt === k ? "border-primary bg-primary/5 ring-1 ring-primary" : "hover:bg-accent/60"}`}>
                <span className="flex items-center gap-1.5 text-sm font-medium"><m.icon className="h-4 w-4" /> {m.short}</span><span className={`block text-[11px] ${a === 0 ? "text-destructive" : "text-muted-foreground"}`}>{a} free</span>
              </button>); })}
          </div>
          <div><Label htmlFor="on-spot-vn" className="text-xs">Vehicle number</Label><Input id="on-spot-vn" value={vn} onChange={(e) => setVn(e.target.value.toUpperCase())} placeholder="CG07AB1234" className="h-12 font-mono text-base mt-1" autoComplete="off" data-testid="on-spot-vehicle-number" /></div>
          <div><Label className="text-xs">Duration</Label>
            <div className="flex flex-wrap gap-2 mt-1">{[30, 60, 120, 180, 240].filter((d) => d >= minM && d <= maxM).map((d) => <button key={d} type="button" onClick={() => setDuration(d)} data-testid={`on-spot-duration-${d}`} className={`rounded-full border px-3 py-2 text-sm min-h-[44px] ${duration === d ? "border-primary bg-primary text-primary-foreground" : "hover:bg-accent"}`}>{fmtMinutes(d)}</button>)}</div></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label htmlFor="on-spot-name" className="text-xs">Driver name (optional)</Label><Input id="on-spot-name" value={name} onChange={(e) => setName(e.target.value)} className="h-11 mt-1" data-testid="on-spot-name" /></div>
            <div><Label htmlFor="on-spot-mobile" className="text-xs">Mobile (optional)</Label><Input id="on-spot-mobile" value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))} inputMode="numeric" className="h-11 mt-1" data-testid="on-spot-mobile" /></div>
          </div>
          <div className="rounded-lg bg-accent/50 p-3 flex items-center justify-between"><span className="text-sm text-muted-foreground">{VT_META[vt].label} · {fmtMinutes(duration)}</span><span className="text-2xl font-semibold tabular-nums font-heading" data-testid="on-spot-fare">{fmtINR(fare)}</span></div>
          <div><Label className="text-xs">Payment</Label><div className="mt-1"><MarshalPayModes modes={me?.payment_modes} value={mode} onChange={setMode} allowNone testId="on-spot-mode" /></div></div>
        </div>
        <DialogFooter className="gap-2 sm:gap-0"><Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button><Button className="h-12" onClick={go} disabled={busy || vn.replace(/[^A-Z0-9]/g, "").length < 4} data-testid="on-spot-submit">{busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-1.5" />} {mode === "none" ? "Enter · pay at exit" : `Enter & collect ${fmtINR(fare)}`}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
